import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getImportJob,
  listImportJobs,
  rollbackImportJob,
} from "../api/clientImport";
import { getCurrentSalonId, getCurrentSalonName } from "../utils/currentSalon";
import SBINModal from "../components/modals/SBINModal";
import {
  MODAL_TEXT,
  LABEL_TEXT,
  FIELD_CLASS,
  BTN_OUTLINE,
  BTN_DANGER,
} from "../ui/modalStyles";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

function buildPageRange(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const output = [];
  let last = 0;
  for (const page of sorted) {
    if (page - last > 1) {
      output.push("ellipsis");
    }
    output.push(page);
    last = page;
  }
  return output;
}

export default function ClientImportHistory() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);

  const [rollbackConfirmOpen, setRollbackConfirmOpen] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackError, setRollbackError] = useState("");
  const [rollbackReport, setRollbackReport] = useState(null);

  const storedToken = token;

  useEffect(() => {
    if (!storedToken) {
      navigate("/login?next=/manager/manage/client-imports", { replace: true });
      return;
    }
    if (!salonId) {
      setError("Select a salon to view import history.");
      const timeout = setTimeout(() => {
        navigate("/my-salons", { replace: true });
      }, 600);
      return () => clearTimeout(timeout);
    }
  }, [storedToken, salonId, navigate]);

  const loadJobs = async () => {
    if (!salonId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listImportJobs({
        salon_id: salonId,
        page,
        per_page: perPage,
      });
      setJobs(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError("Unable to load import history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, [salonId, page, perPage]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pages = useMemo(() => buildPageRange(page, totalPages), [page, totalPages]);

  const renderPagination = () => (
    <div className="flex items-center gap-1 text-sm">
      <button
        type="button"
        onClick={() => setPage(Math.max(1, page - 1))}
        disabled={page <= 1}
        className="h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
      >
        {"<"}
      </button>
      {pages.map((entry, idx) =>
        entry === "ellipsis" ? (
          <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">
            ...
          </span>
        ) : (
          <button
            key={entry}
            type="button"
            onClick={() => setPage(entry)}
            className={`h-10 w-10 rounded-full border text-sm md:text-base ${
              entry === page
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            {entry}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => setPage(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className="h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
      >
        {">"}
      </button>
    </div>
  );

  const handleOpenDetail = async (jobId) => {
    setSelectedJobId(jobId);
    setDetailOpen(true);
    setDetailError("");
    setRollbackReport(null);
    setRollbackError("");
    setDetailLoading(true);
    try {
      const data = await getImportJob(jobId);
      setJobDetail(data);
    } catch (err) {
      setDetailError("Unable to load import details.");
      setJobDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRollback = async () => {
    if (!selectedJobId) return;
    setRollbackLoading(true);
    setRollbackError("");
    try {
      const report = await rollbackImportJob(selectedJobId);
      setRollbackReport(report);
      setRollbackConfirmOpen(false);
      await loadJobs();
      const detail = await getImportJob(selectedJobId);
      setJobDetail(detail);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setRollbackError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to roll back import."
      );
    } finally {
      setRollbackLoading(false);
    }
  };

  const canRollback = (job) =>
    job?.status === "committed" && !job?.rolled_back_at;

  return (
    <div className="manageSectionShell">
      <div
        className={`manageSectionWide manage-section-wide manage-section-compact ${MODAL_TEXT}`}
      >
      <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between bg-rose-500 px-6 py-4">
          <h1 className="text-xl md:text-2xl font-semibold text-white">
            Client Imports
          </h1>
          <button
            type="button"
            onClick={() => navigate("/clients")}
            className={`${BTN_OUTLINE} border-white/40 text-white hover:border-white`}
          >
            Back to Clients
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-slate-700">
              <span className={`${LABEL_TEXT} uppercase`}>Salon</span>
              <span className={`${FIELD_CLASS} bg-slate-50`}>
                {salonName || "Unknown salon"}
                {salonId ? ` (ID ${salonId})` : ""}
              </span>
            </div>

            <div className="flex items-center gap-2 text-slate-700">
              <span className={`${LABEL_TEXT} uppercase`}>Show</span>
              <select
                value={perPage}
                onChange={(event) => {
                  setPage(1);
                  setPerPage(Number(event.target.value));
                }}
                className={FIELD_CLASS}
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className={`${LABEL_TEXT} uppercase`}>entries</span>
            </div>
          </div>

          {error && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-sm text-slate-500">Loading import history...</div>
          ) : (
            <div className="space-y-3">
              {renderPagination()}
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <table className="min-w-full">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Imported on</th>
                      <th className="px-3 py-2">File name</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Created</th>
                      <th className="px-3 py-2">Updated</th>
                      <th className="px-3 py-2">Skipped</th>
                      <th className="px-3 py-2">Failed</th>
                      <th className="px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm md:text-base text-slate-800">
                    {jobs.map((job) => (
                      <tr
                        key={job.id}
                        className="border-t border-slate-100 hover:bg-slate-50"
                      >
                        <td className="px-3 py-2">
                          {job.created_at
                            ? new Date(job.created_at).toLocaleString()
                            : "-"}
                        </td>
                        <td className="px-3 py-2">{job.original_filename || "-"}</td>
                        <td className="px-3 py-2 capitalize">{job.status || "-"}</td>
                        <td className="px-3 py-2">{job.summary?.created ?? 0}</td>
                        <td className="px-3 py-2">{job.summary?.updated ?? 0}</td>
                        <td className="px-3 py-2">{job.summary?.skipped ?? 0}</td>
                        <td className="px-3 py-2">{job.summary?.failed ?? 0}</td>
                        <td className="px-3 py-2">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenDetail(job.id)}
                              className="text-slate-700 underline"
                            >
                              View
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedJobId(job.id);
                                setRollbackConfirmOpen(true);
                              }}
                              className="text-rose-600 underline disabled:text-slate-300"
                              disabled={!canRollback(job)}
                            >
                              Rollback
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {jobs.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-3 py-6 text-center text-sm md:text-base text-slate-500"
                        >
                          No import jobs found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="text-xs md:text-sm text-slate-500">
                Showing {total === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
                {Math.min(page * perPage, total)} of {total} entries
              </div>
            </div>
          )}
        </div>
      </div>

      <SBINModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Import Details"
        size="xl"
        fitContent
      >
        {detailLoading ? (
          <div className="text-sm text-slate-500">Loading details...</div>
        ) : detailError ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
            {detailError}
          </div>
        ) : jobDetail ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Status
                </div>
                <div className="mt-1 text-sm font-semibold text-slate-900 capitalize">
                  {jobDetail.status || "-"}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Created
                </div>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {jobDetail.summary?.created ?? 0}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Updated
                </div>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {jobDetail.summary?.updated ?? 0}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Failed
                </div>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {jobDetail.summary?.failed ?? 0}
                </div>
              </div>
            </div>

            {jobDetail.rollback_summary && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
                <div className="text-sm font-semibold text-slate-900">
                  Rollback Summary
                </div>
                <div className="text-sm text-slate-700">
                  {jobDetail.rollback_summary.deleted_created_clients || 0} deleted,
                  {jobDetail.rollback_summary.reverted_updated_clients || 0} reverted,
                  {jobDetail.rollback_summary.skipped || 0} skipped.
                </div>
              </div>
            )}

            {rollbackReport && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm md:text-base text-emerald-700">
                Rollback complete. {rollbackReport.deleted_created_clients} deleted,
                {rollbackReport.reverted_updated_clients} reverted,{" "}
                {rollbackReport.skipped} skipped.
              </div>
            )}

            {(jobDetail.rollback_summary?.skipped_details || []).length > 0 && (
              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <table className="min-w-full">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Client ID</th>
                      <th className="px-3 py-2">Action</th>
                      <th className="px-3 py-2">Reasons</th>
                      <th className="px-3 py-2">Changed Fields</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm md:text-base text-slate-700">
                    {jobDetail.rollback_summary.skipped_details.map((entry, idx) => (
                      <tr
                        key={`${entry.client_id}-${idx}`}
                        className="border-t border-slate-100 hover:bg-slate-50"
                      >
                        <td className="px-3 py-2">{entry.client_id}</td>
                        <td className="px-3 py-2 uppercase text-xs font-semibold text-slate-500">
                          {entry.action}
                        </td>
                        <td className="px-3 py-2">
                          {(entry.reasons || []).join(", ")}
                        </td>
                        <td className="px-3 py-2">
                          {(entry.changed_fields || []).join(", ") || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end gap-2">
              {canRollback(jobDetail) && (
                <button
                  type="button"
                  onClick={() => setRollbackConfirmOpen(true)}
                  className={BTN_DANGER}
                  disabled={rollbackLoading}
                >
                  Rollback Import
                </button>
              )}
              <button
                type="button"
                onClick={() => setDetailOpen(false)}
                className={BTN_OUTLINE}
              >
                Close
              </button>
            </div>
          </div>
        ) : null}
      </SBINModal>

      <SBINModal
        isOpen={rollbackConfirmOpen}
        onClose={() => setRollbackConfirmOpen(false)}
        title="Confirm Rollback"
        size="lg"
        fitContent
      >
        <div className="space-y-4">
          <p className="text-sm md:text-base text-slate-700">
            Created clients will be deleted only if they have no appointments,
            tickets, or other activity. Updated clients revert only if they have not
            been modified since the import.
          </p>
          {rollbackError && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
              {rollbackError}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setRollbackConfirmOpen(false)}
              className={BTN_OUTLINE}
              disabled={rollbackLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRollback}
              className={BTN_DANGER}
              disabled={rollbackLoading}
            >
              {rollbackLoading ? "Rolling back..." : "Confirm Rollback"}
            </button>
          </div>
        </div>
      </SBINModal>
      </div>
    </div>
  );
}
